import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import api from '../../utils/api';
import MemberProfileAvatar from '../../components/MemberProfileAvatar';

export default function MemberProfile() {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-portal/login');
      return;
    }

    fetchProfile();
  }, [navigate]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('memberToken');
      api.defaults.headers.common['x-auth-token'] = token;

      const response = await api.get('/member-portal/profile');
      setMember(response.data.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('memberToken');
        localStorage.removeItem('member');
        navigate('/member-portal/login');
      } else {
        setError(err.response?.data?.message || 'Failed to fetch profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Active: 'success',
      Pending: 'warning',
      Suspended: 'error',
      Withdrawn: 'default',
      Alumni: 'info',
      Deceased: 'default',
      Minor: 'secondary',
      Pensioners: 'info',
      Students: 'primary',
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !member) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">{error || 'Profile not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/member-portal')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Box>
      <Container maxWidth="md" sx={{ mb: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/member-portal')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate('/member-portal/profile/edit')}
          >
            Edit Profile
          </Button>
        </Box>

        <Paper sx={{ p: 3 }}>
          {/* Profile Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <MemberProfileAvatar
              member={member}
              size={80}
              sx={{ mr: 3, fontSize: '2rem' }}
            />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" gutterBottom>
                {member.first_name} {member.last_name}
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {member.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {member.phone}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={member.status}
                  color={getStatusColor(member.status)}
                  size="small"
                />
                {member.member_id && (
                  <Chip
                    label={`ID: ${member.member_id}`}
                    variant="outlined"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
                {member.association_number && (
                  <Chip
                    label={`Association: ${member.association_number}`}
                    variant="outlined"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Personal Information */}
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Personal Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                First Name
              </Typography>
              <Typography variant="body1">{member.first_name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Last Name
              </Typography>
              <Typography variant="body1">{member.last_name || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Gender
              </Typography>
              <Typography variant="body1">{member.gender || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Date of Birth
              </Typography>
              <Typography variant="body1">{formatDate(member.date_of_birth)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Member Type
              </Typography>
              <Typography variant="body1">{member.member_type || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Date of Joining
              </Typography>
              <Typography variant="body1">{formatDate(member.date_of_joining)}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Contact Information */}
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Contact Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">{member.email || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Phone
              </Typography>
              <Typography variant="body1">{member.phone || '-'}</Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Location Information */}
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Location
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Region
              </Typography>
              <Typography variant="body1">{member.region || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Constituency
              </Typography>
              <Typography variant="body1">{member.constituency || '-'}</Typography>
            </Grid>
          </Grid>

          {/* Education & Employment */}
          {(member.education_level || member.profession || member.employment_status) && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Education & Employment
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {member.education_level && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Education Level
                    </Typography>
                    <Typography variant="body1">{member.education_level}</Typography>
                  </Grid>
                )}
                {member.profession && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Profession
                    </Typography>
                    <Typography variant="body1">{member.profession}</Typography>
                  </Grid>
                )}
                {member.employment_status && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Employment Status
                    </Typography>
                    <Typography variant="body1">{member.employment_status}</Typography>
                  </Grid>
                )}
                {member.institution && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Institution
                    </Typography>
                    <Typography variant="body1">{member.institution}</Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* Student Information */}
          {member.is_student && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Student Information
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {member.student_id_number && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Student ID
                    </Typography>
                    <Typography variant="body1">{member.student_id_number}</Typography>
                  </Grid>
                )}
                {member.hall_of_residence && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Hall of Residence
                    </Typography>
                    <Typography variant="body1">{member.hall_of_residence}</Typography>
                  </Grid>
                )}
                {member.program_of_study && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Program of Study
                    </Typography>
                    <Typography variant="body1">{member.program_of_study}</Typography>
                  </Grid>
                )}
                {member.level && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Level
                    </Typography>
                    <Typography variant="body1">{member.level}</Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
